import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import dayjs from 'dayjs';
import { CalendarService } from '../services/CalendarService';
import { MasterDataService } from '../services/MasterDataService';
import { ProvidersService } from '../services/ProvidersService';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const SLOT_HEIGHT_PX = 50;
const SERVICE_CATEGORY_COLORS = {
  Wellness: '#1976d2',
  Massage: '#388e3c',
  Therapy: '#d32f2f',
  Fitness: '#f57c00',
  Nutrition: '#7b1fa2',
  'Mental Health': '#c2185b',
  Skincare: '#ffc107',
  default: '#757575'
};

// djb2 string hash for fallback color selection
function djb2Hash(str) {
  if (!str) return 0;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

function getColorByCategory(serviceName, categoryMap) {
  if (!serviceName) return SERVICE_CATEGORY_COLORS.default;

  const category = categoryMap[serviceName];
  if (category && SERVICE_CATEGORY_COLORS[category]) {
    return SERVICE_CATEGORY_COLORS[category];
  }

  // Fallback: hash the full service name to pick from palette
  const colors = Object.values(SERVICE_CATEGORY_COLORS);
  const hash = djb2Hash(serviceName);
  return colors[hash % colors.length];
}

function ListView({ appointments, view, setView, filter, setFilter, providerNameMap }) {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredAndSortedAppointments = () => {
    const now = new Date();
    let filtered = appointments;

    if (filter === 'future') {
      filtered = appointments.filter(apt => new Date(apt.start) >= now);
      filtered.sort((a, b) => new Date(a.start) - new Date(b.start));
    } else if (filter === 'past') {
      filtered = appointments.filter(apt => new Date(apt.start) < now);
      filtered.sort((a, b) => new Date(b.start) - new Date(a.start));
    } else {
      filtered.sort((a, b) => new Date(b.start) - new Date(a.start));
    }

    return filtered;
  };

  const filteredAppointments = getFilteredAndSortedAppointments();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, newView) => newView && setView(newView)}
          size="small"
        >
          <ToggleButton value="list">List</ToggleButton>
          <ToggleButton value="calendar">Calendar</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, newFilter) => newFilter && setFilter(newFilter)}
          size="small"
        >
          <ToggleButton value="future">Future</ToggleButton>
          <ToggleButton value="past">Past</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {filteredAppointments.length === 0 ? (
        <Alert severity="info">
          {filter === 'future' && 'You have no upcoming appointments.'}
          {filter === 'past' && 'You have no past appointments.'}
          {filter === 'all' && 'You have no appointments yet.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Date & Time</strong></TableCell>
                <TableCell><strong>Service</strong></TableCell>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell align="right"><strong>Duration (min)</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAppointments.map((appointment) => (
                <TableRow key={appointment.id} hover>
                  <TableCell>{formatDate(appointment.start)}</TableCell>
                  <TableCell>{appointment.service || 'N/A'}</TableCell>
                  <TableCell>{providerNameMap[appointment.providerId] || 'N/A'}</TableCell>
                  <TableCell align="right">{appointment.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function HourlyWeekGrid({ weekStartDate, appointments, categoryMap, filter, providerNameMap }) {
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    weekDays.push(weekStartDate.add(i, 'day'));
  }

  const allSlots = [];
  for (let h = 0; h < 24; h++) {
    allSlots.push({ hour: h, minute: 0 });
    allSlots.push({ hour: h, minute: 30 });
  }

  // Build appointments map by date
  const appointmentsMap = {};
  const now = new Date();
  const filteredAppts = appointments.filter(apt => {
    const aptDate = new Date(apt.start);
    if (filter === 'future') return aptDate >= now;
    if (filter === 'past') return aptDate < now;
    return true;
  });

  filteredAppts.forEach(apt => {
    const dateStr = dayjs(apt.start).format('YYYY-MM-DD');
    if (!appointmentsMap[dateStr]) {
      appointmentsMap[dateStr] = [];
    }
    appointmentsMap[dateStr].push(apt);
  });

  // Calculate visible hour range
  const allAppointmentMinutes = [];
  Object.values(appointmentsMap).forEach(dayAppts => {
    if (Array.isArray(dayAppts)) {
      dayAppts.forEach(apt => {
        if (apt.start) {
          const d = dayjs(apt.start);
          allAppointmentMinutes.push(d.hour() * 60 + d.minute());
        }
      });
    }
  });

  let appointmentHourRange = null;
  if (allAppointmentMinutes.length > 0) {
    const minMinutes = Math.min(...allAppointmentMinutes);
    const maxMinutes = Math.max(...allAppointmentMinutes);
    appointmentHourRange = {
      start: Math.floor(minMinutes / 60),
      end: Math.ceil(maxMinutes / 60)
    };
  }

  const defaultStart = 9;
  const defaultEnd = 17;
  const visibleStart = appointmentHourRange
    ? Math.min(defaultStart, appointmentHourRange.start)
    : defaultStart;
  const visibleEnd = appointmentHourRange
    ? Math.max(defaultEnd, appointmentHourRange.end)
    : defaultEnd;

  const visibleSlots = allSlots.filter(s => s.hour >= visibleStart && s.hour < visibleEnd);

  return (
    <Box sx={{ overflowX: 'auto', mb: 3 }}>
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 60 }}>Time</TableCell>
            {weekDays.map(day => (
              <TableCell key={day.format('YYYY-MM-DD')} sx={{ fontWeight: 600, textAlign: 'center', minWidth: 120 }}>
                <Box>{day.format('ddd')}</Box>
                <Box sx={{ fontSize: '0.85rem', color: 'textSecondary' }}>{day.format('MMM D')}</Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleSlots.map((slot) => (
            <TableRow key={`${slot.hour}-${slot.minute}`} sx={{ height: SLOT_HEIGHT_PX }}>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', height: SLOT_HEIGHT_PX, py: 0, px: 1 }}>
                {String(slot.hour).padStart(2, '0')}:{String(slot.minute).padStart(2, '0')}
              </TableCell>
              {weekDays.map(day => {
                const dateStr = day.format('YYYY-MM-DD');
                const dayAppts = appointmentsMap[dateStr] || [];
                const slotStartMinutes = slot.hour * 60 + slot.minute;
                const slotEndMinutes = slotStartMinutes + 30;

                const aptsInSlot = dayAppts.filter(apt => {
                  const aptMinutes = dayjs(apt.start).hour() * 60 + dayjs(apt.start).minute();
                  return aptMinutes >= slotStartMinutes && aptMinutes < slotEndMinutes;
                });

                return (
                  <TableCell
                    key={`${dateStr}-${slot.hour}-${slot.minute}`}
                    sx={{
                      bgcolor: '#f5f5f5',
                      py: 0,
                      px: 1,
                      height: SLOT_HEIGHT_PX,
                      position: 'relative',
                      verticalAlign: 'top',
                      overflow: 'visible',
                    }}
                  >
                    {aptsInSlot.length > 0 && (
                      <>
                        {aptsInSlot.map((apt, aIdx) => {
                          const durationMinutes = apt.length || (apt.end ? dayjs(apt.end).diff(dayjs(apt.start), 'minute') : 30);
                          const appointmentHeight = Math.max(32, (durationMinutes / 30) * SLOT_HEIGHT_PX);
                          const aptStartMinutes = dayjs(apt.start).hour() * 60 + dayjs(apt.start).minute();
                          const slotOffsetMinutes = Math.max(0, aptStartMinutes - slotStartMinutes);
                          const slotOffsetPx = (slotOffsetMinutes / 30) * SLOT_HEIGHT_PX;
                          const stackOffset = aIdx * 4;
                          const topPx = slotOffsetPx + stackOffset;
                          const bgColor = getColorByCategory(apt.service, categoryMap);
                          const tooltipEnd = apt.end ? dayjs(apt.end) : dayjs(apt.start).add(durationMinutes, 'minute');
                          const providerName = providerNameMap[apt.providerId];
                          const tooltipTitle = (
                            <Box sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                              <Box sx={{ fontWeight: 600 }}>
                                {dayjs(apt.start).format('HH:mm')} - {tooltipEnd.format('HH:mm')}
                              </Box>
                              {apt.service && <Box>{apt.service}</Box>}
                              {providerName && <Box>Provider: {providerName}</Box>}
                            </Box>
                          );

                          return (
                            <Tooltip key={aIdx} title={tooltipTitle} placement="top">
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: topPx,
                                  left: '4px',
                                  right: '4px',
                                  height: appointmentHeight,
                                  p: 0.5,
                                  bgcolor: bgColor,
                                  color: '#fff',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  border: `2px solid ${bgColor}`,
                                  overflow: 'hidden',
                                  zIndex: 2 + aIdx,
                                  cursor: 'help',
                                }}
                              >
                                <Box sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                                  {dayjs(apt.start).format('HH:mm')}
                                </Box>
                                {apt.service && (
                                  <Box sx={{ fontSize: '0.65rem', mt: 0.25 }}>
                                    {apt.service}
                                  </Box>
                                )}
                                {providerNameMap[apt.providerId] && (
                                  <Box sx={{ fontSize: '0.6rem', mt: 0.25, opacity: 0.9 }}>
                                    {providerNameMap[apt.providerId]}
                                  </Box>
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function CalendarView({ appointments, view, setView, filter, setFilter, categoryMap, providerNameMap }) {
  const now = dayjs();
  const [weekStartDate, setWeekStartDate] = useState(getMonday(now));

  function getMonday(date) {
    const d = dayjs(date);
    const dayNum = d.day();
    const daysToMonday = dayNum === 0 ? -6 : 1 - dayNum;
    return d.add(daysToMonday, 'day').startOf('day');
  }

  const handlePrevWeek = () => {
    setWeekStartDate(weekStartDate.subtract(1, 'week'));
  };

  const handleNextWeek = () => {
    setWeekStartDate(weekStartDate.add(1, 'week'));
  };

  const handleThisWeek = () => {
    setWeekStartDate(getMonday(dayjs()));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, newView) => newView && setView(newView)}
          size="small"
        >
          <ToggleButton value="list">List</ToggleButton>
          <ToggleButton value="calendar">Calendar</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, newFilter) => newFilter && setFilter(newFilter)}
          size="small"
        >
          <ToggleButton value="future">Future</ToggleButton>
          <ToggleButton value="past">Past</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <button onClick={handlePrevWeek}>Prev</button>
          <Typography sx={{ minWidth: 220, textAlign: 'center', fontWeight: 500 }}>
            {weekStartDate.format('MMM D')} - {weekStartDate.add(6, 'day').format('MMM D, YYYY')}
          </Typography>
          <button onClick={handleNextWeek}>Next</button>
          <button onClick={handleThisWeek}>This Week</button>
        </Box>
      </Box>

      {appointments.length === 0 ? (
        <Alert severity="info">
          {filter === 'future' && 'You have no upcoming appointments.'}
          {filter === 'past' && 'You have no past appointments.'}
          {filter === 'all' && 'You have no appointments yet.'}
        </Alert>
      ) : (
        <HourlyWeekGrid
          weekStartDate={weekStartDate}
          appointments={appointments}
          categoryMap={categoryMap}
          filter={filter}
          providerNameMap={providerNameMap}
        />
      )}
    </Box>
  );
}

function Appointments() {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list');
  const [filter, setFilter] = useState('future');
  const [categoryMap, setCategoryMap] = useState({});
  const [providerNameMap, setProviderNameMap] = useState({});

  useEffect(() => {
    if (keycloak && keycloak.authenticated) {
      CalendarService.keycloak = keycloak;
      loadAppointments();
      loadCategories();
    }
  }, [keycloak]);

  const loadCategories = async () => {
    try {
      const categories = await MasterDataService.getAllCategories();
      if (categories && Array.isArray(categories)) {
        // Build a map: service name -> category name
        const map = {};
        categories.forEach(category => {
          if (category.services && Array.isArray(category.services)) {
            category.services.forEach(service => {
              map[service.serviceName] = category.categoryName;
            });
          }
        });
        setCategoryMap(map);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      // Fallback gracefully; use hash-based colors
    }
  };

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const userId = keycloak.tokenParsed.sub;
      const response = await CalendarService.getClientBookings(userId);

      if (response && response.appointments) {
        setAppointments(response.appointments);
        // Fetch provider names for all unique provider IDs
        await loadProviderNames(response.appointments);
      } else {
        setAppointments([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Failed to load appointments');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProviderNames = async (appts) => {
    try {
      // Get unique provider IDs from appointments
      const uniqueProviderIds = [...new Set(appts.map(apt => apt.providerId).filter(Boolean))];

      if (uniqueProviderIds.length === 0) return;

      const map = {};

      // Fetch provider details for each unique ID
      for (const providerId of uniqueProviderIds) {
        try {
          const provider = await ProvidersService.getSingleProvider(providerId);
          // Use either firstName+lastName or name or displayName
          const providerName = provider.firstName && provider.lastName
            ? `${provider.firstName} ${provider.lastName}`
            : provider.name || provider.displayName || 'Unknown Provider';
          map[providerId] = providerName;
        } catch (err) {
          console.warn(`Failed to load provider ${providerId}:`, err);
          map[providerId] = 'Unknown Provider';
        }
      }

      setProviderNameMap(map);
    } catch (err) {
      console.error('Error loading provider names:', err);
      // Gracefully degrade - show "Unknown Provider" instead
    }
  };

  if (!keycloak.authenticated) {
    return (
      <Container>
        <Typography variant="h6">Please log in to view your appointments.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Helmet>
        <title>My Appointments - Pair SelfCare</title>
      </Helmet>
      <Typography variant="h4" gutterBottom>
        My Appointments
      </Typography>

      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && appointments.length === 0 && (
        <Alert severity="info">
          You have no appointments yet.
        </Alert>
      )}

      {!loading && appointments.length > 0 && view === 'list' && (
        <ListView
          appointments={appointments}
          view={view}
          setView={setView}
          filter={filter}
          setFilter={setFilter}
          providerNameMap={providerNameMap}
        />
      )}

      {!loading && appointments.length > 0 && view === 'calendar' && (
        <CalendarView
          appointments={appointments}
          view={view}
          setView={setView}
          filter={filter}
          setFilter={setFilter}
          categoryMap={categoryMap}
          providerNameMap={providerNameMap}
        />
      )}
    </Container>
  );
}

export default Appointments;
